import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Dialog from 'src/componentes/dialog';
import Button from 'src/componentes/button';
import Panel from 'src/componentes/panel-collapse';
import { capitalizeFirstLetter } from 'src/utils/string';
import ItemList from 'src/paginas/item';
import http from 'src/services/httpService';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import { MODULOS } from 'src/utils/constants';
import useAuth from 'src/hooks/useAuth';

const RiscoItensAssociadosList = ({
  model,
  risco,
  habilitarIndicadoresRisco = false,
  showOnlyProjects = false,
  modules,
  ...other
}) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const dialogRef = useRef();

  const [indicadoresRisco, setIndicadoresRisco] = useState(() =>
    model.value ? model.value.filter((i) => i.tipo.id == 17) : []
  );
  const [indicadores, setIndicadores] = useState(() =>
    model.value ? model.value.filter((i) => i.tipo.id == 1) : []
  );
  const [processos, setProcessos] = useState(() =>
    model.value ? model.value.filter((i) => i.tipo.id == 5) : []
  );
  const [projetos, setProjetos] = useState(() =>
    model.value ? model.value.filter((i) => i.tipo.id == 4) : []
  );
  const [objetivos, setObjetivos] = useState(() =>
    model.value ? model.value.filter((i) => i.tipo.id == 3) : []
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateModel = () => {
    model.requestChange([
      ...indicadoresRisco,
      ...indicadores,
      ...processos,
      ...projetos,
      ...objetivos,
    ]);
  };

  const handleOkClick = () => {
    handleUpdateModel();
    dialogRef.current.close();
  };

  const handleCancelarClick = () => {
    dialogRef.current.close();
  };

  const handleCustomSelectSearch = async (selecteds, tipoId) => {
    const itens = selecteds ? selecteds : [];

    switch (tipoId) {
      case 1: {
        setIndicadores(itens);
        break;
      }
      case 5: {
        setIsLoading(true);
        const addedProcesso = itens.find((item) => !processos.includes(item));
        setProcessos(itens);
        if (addedProcesso && risco != null && risco.matrizRisco != null) {
          const resultMatriz = await http.post(
            '/Processo/ObterMatrizProcesso',
            {
              idProcesso: addedProcesso?.id,
            }
          );

          if (
            resultMatriz &&
            resultMatriz.data &&
            resultMatriz.data.id != risco.matrizRisco.id
          ) {
            ContentManager.addContent(
              <Alert
                message={intl.formatMessage(
                  {
                    id: 'processoSelecionadoTemUmaMatrizDiferenteDoRiscoAtual',
                  },
                  {
                    risco: resources.risco,
                    processo: resources.processo,
                  }
                )}
              />
            );
          }
        }
        setIsLoading(false);
        break;
      }
      case 4: {
        setProjetos(itens);
        break;
      }
      case 3: {
        setObjetivos(itens);
        break;
      }
      case 17: {
        setIndicadoresRisco(itens);
        break;
      }
    }
  };

  const handleGetList = (tipoId) => {
    switch (tipoId) {
      case 1:
        return indicadores;
      case 5:
        return processos;
      case 4:
        return projetos;
      case 3:
        return objetivos;
      case 17:
        return indicadoresRisco;
    }
  };

  const handleList = (tipoId, hierarchy = true, pageSize = 10) => {
    const list = handleGetList(tipoId) || [];

    return (
      <ItemList
        multiple
        select
        selectRealTime
        selectedCallback={(itens) => handleCustomSelectSearch(itens, tipoId)}
        selectedItems={list}
        selectedIds={list}
        pageSize={pageSize}
        showPagination={true}
        hierarchy={hierarchy}
        parameters={{ tipoId }}
        maxExpandSize={hierarchy}
      ></ItemList>
    );
  };

  const actions = [
    <Button
      key="cancelarRiscoItensAssociados"
      disabled={isLoading}
      onClick={handleCancelarClick}
      color="secondary"
    >
      {intl.formatMessage({ id: 'label.cancelar' })}
    </Button>,

    <Button
      key="salvarRiscoItensAssociados"
      className="ml-2"
      type="primary"
      disabled={isLoading}
      onClick={handleOkClick}
      htmlType="submit"
    >
      {intl.formatMessage({ id: 'label.salvar' })}
    </Button>,
  ];

  const possuiModuloGE =
    !modules ||
    modules.find((m) => m.id === MODULOS.GESTAO_ESTRATEGICA && m.ativo);
  const possuiModuloMO =
    !modules ||
    modules.find((m) => m.id === MODULOS.MATRIZ_ORCAMENTARIA && m.ativo);
  const possuiModuloRV =
    !modules ||
    modules.find((m) => m.id === MODULOS.REMUNERACAO_VARIAVEL && m.ativo);
  const possuiModuloPROJETO =
    !modules ||
    modules.find((m) => m.id === MODULOS.ESCRITORIO_PROJETOS && m.ativo);
  const possuiGEMORV = possuiModuloGE || possuiModuloMO || possuiModuloRV;

  return (
    <Dialog
      {...other}
      ref={dialogRef}
      width="80%"
      height="80%"
      actions={actions}
      padContent
    >
      {habilitarIndicadoresRisco && (
        <Panel
          header={capitalizeFirstLetter(
            intl.formatMessage(
              { id: 'indicadorRisco' },
              { risco: resources.risco }
            )
          )}
          open={false}
        >
          {handleList(17, false)}
        </Panel>
      )}
      {possuiGEMORV && !showOnlyProjects && (
        <Panel header={capitalizeFirstLetter(resources.indicador)} open={false}>
          {handleList(1, false)}
        </Panel>
      )}
      {!showOnlyProjects && (
        <Panel header={capitalizeFirstLetter(resources.processos)} open={false}>
          {handleList(5, true)}
        </Panel>
      )}
      {(possuiModuloPROJETO || possuiGEMORV) && (
        <Panel header={capitalizeFirstLetter(resources.projeto)} open={false}>
          {handleList(4, true)}
        </Panel>
      )}
      {possuiGEMORV && !showOnlyProjects && (
        <Panel
          header={capitalizeFirstLetter(resources.objetivoestrategico)}
          open={false}
        >
          {handleList(3, false)}
        </Panel>
      )}
    </Dialog>
  );
};

export default RiscoItensAssociadosList;
